import { posthog } from '../../lib/posthog';

/**
 * The onboarding funnel (plan slice 1). One place for the event names, so the
 * PostHog funnel and the code cannot drift apart.
 */
export type FunnelEvent =
  | { event: 'signup_started' }
  | { event: 'account_created' }
  | { event: 'handle_claimed' }
  | { event: 'onboarding_step_viewed'; step: string }
  | { event: 'onboarding_step_skipped'; step: string }
  | { event: 'avatar_set'; kind: 'seal' | 'face' | 'upload' }
  | { event: 'bio_set' }
  | { event: 'stamp_book_viewed' }
  | { event: 'correspondence_unlocked' }
  | { event: 'correspondence_locked' }
  // Hide and block (2026-10-29): only the act, never who.
  | { event: 'correspondence_set_aside' }
  | { event: 'correspondent_blocked' }
  | { event: 'overture_removed' }
  | {
      event: 'share_opened';
      channel: 'whatsapp' | 'sheet' | 'copy' | 'email' | 'qr' | 'story' | 'bio';
      from?: 'delivered_prompt';
    }
  | { event: 'share_kit_viewed'; from: 'tour' | 'profile' | 'menu' | 'getting_started' | 'home_panel' | 'delivered_prompt' }
  | { event: 'bio_line_copied'; platform: string }
  | { event: 'invite_link_opened'; via: string | null }
  | { event: 'first_letter_sealed' }
  | { event: 'about_opened' }
  // Reach (2026-10-11): installing the app and saying yes to notifications.
  | { event: 'reach_prompt_shown'; kind: 'notify' | 'ios-install'; reason: 'sealed' | 'incoming' | 'email' }
  | { event: 'reach_prompt_accepted'; kind: 'notify' | 'ios-install'; reason: 'sealed' | 'incoming' | 'email' }
  | { event: 'reach_prompt_declined'; kind: 'notify' | 'ios-install'; reason: 'sealed' | 'incoming' | 'email' }
  | { event: 'push_permission_result'; result: NotificationPermission; reason: 'sealed' | 'incoming' | 'email' | 'home' }
  | { event: 'install_prompt_shown' }
  | { event: 'install_prompt_result'; outcome: 'accepted' | 'dismissed' | 'unavailable' | 'not_now' }
  | { event: 'app_installed' }
  // After one of my letters lands: "Who else deserves a letter from you?" (2026-10-19).
  | { event: 'delivered_prompt_shown'; surface: DeliveredPromptSurface }
  | { event: 'delivered_prompt_clicked'; surface: DeliveredPromptSurface; action: 'share' | 'more_ways' }
  | { event: 'delivered_prompt_dismissed'; surface: DeliveredPromptSurface };

/** Where the delivered prompt sat: the short-phone nudge, or its own card in the list. */
export type DeliveredPromptSurface = 'mobile_nudge' | 'list';

export function track({ event, ...props }: FunnelEvent): void {
  posthog.capture(event, props);
}

const FIRST_SEAL_KEY = 'toiyp:first_sealed';

/**
 * The first letter this person seals on this device. PostHog keeps only the
 * first per person in the funnel anyway; the flag just keeps the noise down.
 */
export function trackLetterSealed(): void {
  // Per person, so a second account on a shared phone still counts.
  const key = `${FIRST_SEAL_KEY}:${posthog.get_distinct_id?.() ?? ''}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // Private mode — send it; the funnel takes the first.
  }
  track({ event: 'first_letter_sealed' });
}
